import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  FaHeart,
  FaRegHeart,
  FaShareAlt,
  FaTimes,
} from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { API_BASE } from "../config/api";
import useCart from "../hooks/useCart";
import useWishlist from "../hooks/useWishlist";
import { formatMaterialDisplayLabel } from "../utils/materialLabels";
import {
  getCarouselPosition,
  saveCarouselPosition,
  saveCurrentScrollPosition,
} from "../utils/scrollRestoration";
import { bindStorefrontRefresh } from "../utils/storefrontRefresh";
import "./ProductDetail.css";

const LOW_STOCK_THRESHOLD = 5;
const DEFAULT_MAIN_MEDIA_FRAME_PRESET = "balanced";
const DEFAULT_MAIN_MEDIA_SURFACE = "#ffffff";
const DEFAULT_MAIN_MEDIA_ASPECT_RATIO = 1;
const DEFAULT_MAIN_MEDIA_FOCUS_POINT = { x: 50, y: 50 };
const DEFAULT_MAIN_MEDIA_CONTENT_SCALE = 1;
const DEFAULT_MAIN_MEDIA_PRESENTATION = {
  aspectRatio: DEFAULT_MAIN_MEDIA_ASPECT_RATIO,
  focusX: DEFAULT_MAIN_MEDIA_FOCUS_POINT.x,
  focusY: DEFAULT_MAIN_MEDIA_FOCUS_POINT.y,
  framePreset: DEFAULT_MAIN_MEDIA_FRAME_PRESET,
  contentScale: DEFAULT_MAIN_MEDIA_CONTENT_SCALE,
  surface: DEFAULT_MAIN_MEDIA_SURFACE,
};
const SHIPPING_CARD_ITEMS = [
  "Free delivery above \u20B91000",
  "Delivery within 5\u20138 business days",
];

const formatCurrency = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "₹0";
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

const normalizeBulletLine = (value = "") =>
  String(value || "")
    .replace(/^[\s\-*•]+/, "")
    .trim();

const getShippingCardItems = () => SHIPPING_CARD_ITEMS;

const getDescriptionPoints = (product = {}) => {
  const description = String(product?.description || "").trim();
  const multiLinePoints = description
    .split(/\r?\n+/)
    .map(normalizeBulletLine)
    .filter(Boolean);

  if (multiLinePoints.length > 1) {
    return multiLinePoints;
  }

  if (multiLinePoints.length === 1) {
    const sentencePoints = multiLinePoints[0]
      .split(/\.\s+/)
      .map(normalizeBulletLine)
      .filter(Boolean)
      .map((item) => (/[.!?]$/.test(item) ? item : `${item}.`));

    if (sentencePoints.length > 1) {
      return sentencePoints;
    }
  }

  const factualFallback = [];

  if (description) {
    factualFallback.push(description);
  }

  if (product?.material) {
    factualFallback.push(`Material: ${formatMaterialDisplayLabel(product.material)}`);
  }

  if (product?.type) {
    factualFallback.push(`Style: ${product.type}`);
  }

  if (Array.isArray(product?.availableSizes) && product.availableSizes.length > 0) {
    factualFallback.push(`Available sizes: ${product.availableSizes.join(", ")}`);
  }

  return factualFallback.length > 0
    ? factualFallback
    : ["A refined jewellery piece curated for elevated everyday and occasion styling."];
};

const getPrecautionPoints = (product = {}) => {
  const rawValues = Array.isArray(product?.precautions)
    ? product.precautions
    : typeof product?.precautions === "string"
    ? product.precautions.split(/[,\n]+/)
    : [];

  const normalizedValues = rawValues
    .map(normalizeBulletLine)
    .filter(Boolean);

  return normalizedValues.length > 0
    ? normalizedValues
    : [
        "Keep away from perfumes and harsh chemicals.",
        "Store in a dry place away from moisture and dust.",
      ];
};

const getStockQuantity = (value) => {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) ? Math.max(0, Math.floor(parsedValue)) : 0;
};

const getMainMediaFramePreset = (width, height) => {
  const safeWidth = Number(width);
  const safeHeight = Number(height);

  if (!Number.isFinite(safeWidth) || !Number.isFinite(safeHeight) || safeWidth <= 0 || safeHeight <= 0) {
    return DEFAULT_MAIN_MEDIA_FRAME_PRESET;
  }

  const aspectRatio = safeWidth / safeHeight;

  if (aspectRatio <= 0.84) {
    return "portrait";
  }

  if (aspectRatio >= 1.2) {
    return "landscape";
  }

  return DEFAULT_MAIN_MEDIA_FRAME_PRESET;
};

const clampNumber = (value, min, max) =>
  Math.max(min, Math.min(max, value));

const clampColorChannel = (value) =>
  Math.round(clampNumber(value, 0, 255));

const toHexPair = (value) => clampColorChannel(value).toString(16).padStart(2, "0");

const rgbToHex = ({ r, g, b }) => `#${toHexPair(r)}${toHexPair(g)}${toHexPair(b)}`;

const getCanvasEdgeAverageColor = (imageData, width, height, edgeDepth) => {
  let red = 0;
  let green = 0;
  let blue = 0;
  let weight = 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const isEdgePixel =
        x < edgeDepth
        || x >= width - edgeDepth
        || y < edgeDepth
        || y >= height - edgeDepth;

      if (!isEdgePixel) {
        continue;
      }

      const index = (y * width + x) * 4;
      const alpha = imageData[index + 3] / 255;

      if (alpha < 0.48) {
        continue;
      }

      const pixelWeight = alpha;
      red += imageData[index] * pixelWeight;
      green += imageData[index + 1] * pixelWeight;
      blue += imageData[index + 2] * pixelWeight;
      weight += pixelWeight;
    }
  }

  if (weight < 1) {
    return null;
  }

  return {
    r: red / weight,
    g: green / weight,
    b: blue / weight,
  };
};

const toCssUrlValue = (value) => {
  const normalizedValue = String(value || "").trim();

  if (!normalizedValue) {
    return "none";
  }

  return `url("${normalizedValue.replace(/(["\\])/g, "\\$1")}")`;
};

const getMainMediaSurfaceColor = (imageElement) => {
  if (
    typeof document === "undefined"
    || !imageElement
    || !imageElement.naturalWidth
    || !imageElement.naturalHeight
  ) {
    return DEFAULT_MAIN_MEDIA_SURFACE;
  }

  try {
    const sampleSize = 28;
    const edgeDepth = 4;
    const canvas = document.createElement("canvas");
    canvas.width = sampleSize;
    canvas.height = sampleSize;

    const context = canvas.getContext("2d", {
      willReadFrequently: true,
    });

    if (!context) {
      return DEFAULT_MAIN_MEDIA_SURFACE;
    }

    context.drawImage(imageElement, 0, 0, sampleSize, sampleSize);

    const { data } = context.getImageData(0, 0, sampleSize, sampleSize);
    const averageColor = getCanvasEdgeAverageColor(data, sampleSize, sampleSize, edgeDepth);

    if (!averageColor) {
      return DEFAULT_MAIN_MEDIA_SURFACE;
    }

    if (
      averageColor.r >= 245
      && averageColor.g >= 245
      && averageColor.b >= 245
    ) {
      return DEFAULT_MAIN_MEDIA_SURFACE;
    }

    return rgbToHex(averageColor);
  } catch {
    return DEFAULT_MAIN_MEDIA_SURFACE;
  }
};

const getMainMediaContentFit = (imageElement) => {
  if (
    typeof document === "undefined"
    || !imageElement
    || !imageElement.naturalWidth
    || !imageElement.naturalHeight
  ) {
    return {
      focusX: DEFAULT_MAIN_MEDIA_FOCUS_POINT.x,
      focusY: DEFAULT_MAIN_MEDIA_FOCUS_POINT.y,
      contentScale: DEFAULT_MAIN_MEDIA_CONTENT_SCALE,
    };
  }

  try {
    const longestSide = 144;
    const imageAspectRatio = imageElement.naturalWidth / imageElement.naturalHeight;
    const sampleWidth =
      imageAspectRatio >= 1
        ? longestSide
        : Math.max(72, Math.round(longestSide * imageAspectRatio));
    const sampleHeight =
      imageAspectRatio >= 1
        ? Math.max(72, Math.round(longestSide / imageAspectRatio))
        : longestSide;
    const edgeDepth = Math.max(4, Math.round(Math.min(sampleWidth, sampleHeight) * 0.07));
    const canvas = document.createElement("canvas");
    canvas.width = sampleWidth;
    canvas.height = sampleHeight;

    const context = canvas.getContext("2d", {
      willReadFrequently: true,
    });

    if (!context) {
      return {
        focusX: DEFAULT_MAIN_MEDIA_FOCUS_POINT.x,
        focusY: DEFAULT_MAIN_MEDIA_FOCUS_POINT.y,
        contentScale: DEFAULT_MAIN_MEDIA_CONTENT_SCALE,
      };
    }

    context.drawImage(imageElement, 0, 0, sampleWidth, sampleHeight);

    const { data } = context.getImageData(0, 0, sampleWidth, sampleHeight);
    const backgroundColor =
      getCanvasEdgeAverageColor(data, sampleWidth, sampleHeight, edgeDepth)
      || { r: 255, g: 255, b: 255 };
    const backgroundLuminance =
      (0.2126 * backgroundColor.r) + (0.7152 * backgroundColor.g) + (0.0722 * backgroundColor.b);
    let minX = sampleWidth;
    let minY = sampleHeight;
    let maxX = -1;
    let maxY = -1;
    let contentPixelCount = 0;

    for (let y = 0; y < sampleHeight; y += 1) {
      for (let x = 0; x < sampleWidth; x += 1) {
        const index = (y * sampleWidth + x) * 4;
        const alpha = data[index + 3] / 255;

        if (alpha < 0.08) {
          continue;
        }

        const red = data[index];
        const green = data[index + 1];
        const blue = data[index + 2];
        const colorDistance = Math.sqrt(
          ((red - backgroundColor.r) ** 2)
          + ((green - backgroundColor.g) ** 2)
          + ((blue - backgroundColor.b) ** 2)
        );
        const luminance =
          (0.2126 * red) + (0.7152 * green) + (0.0722 * blue);
        const luminanceDifference = Math.abs(luminance - backgroundLuminance);

        if (colorDistance < 18 && luminanceDifference < 12) {
          continue;
        }

        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
        contentPixelCount += 1;
      }
    }

    if (contentPixelCount < 24 || maxX < minX || maxY < minY) {
      return {
        focusX: DEFAULT_MAIN_MEDIA_FOCUS_POINT.x,
        focusY: DEFAULT_MAIN_MEDIA_FOCUS_POINT.y,
        contentScale: DEFAULT_MAIN_MEDIA_CONTENT_SCALE,
      };
    }

    const normalizedContentWidth = (maxX - minX + 1) / sampleWidth;
    const normalizedContentHeight = (maxY - minY + 1) / sampleHeight;
    const focusX = clampNumber((((minX + maxX + 1) / 2) / sampleWidth) * 100, 8, 92);
    const focusY = clampNumber((((minY + maxY + 1) / 2) / sampleHeight) * 100, 8, 92);
    const contentScale = clampNumber(
      Math.max(1 / normalizedContentWidth, 1 / normalizedContentHeight),
      1.02,
      2.4
    );

    return {
      focusX,
      focusY,
      contentScale,
    };
  } catch {
    return {
      focusX: DEFAULT_MAIN_MEDIA_FOCUS_POINT.x,
      focusY: DEFAULT_MAIN_MEDIA_FOCUS_POINT.y,
      contentScale: DEFAULT_MAIN_MEDIA_CONTENT_SCALE,
    };
  }
};

const getDisplaySku = (product = {}) => {
  const explicitSku = String(product?.sku || "").trim().toUpperCase();

  if (explicitSku) {
    return explicitSku;
  }

  const productId = String(product?._id || "").trim();
  return productId ? `PJ-${productId.slice(-8).toUpperCase()}` : "PJ-00000000";
};

const isVideo = (url) => {
  if (!url) {
    return false;
  }

  const videoExtensions = [".mp4", ".avi", ".mov", ".wmv", ".flv", ".webm"];
  return videoExtensions.some((extension) =>
    url.toLowerCase().includes(extension)
  );
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart, isInCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const mainImageRef = useRef(null);
  const mainMediaPresentationCacheRef = useRef(new Map());
  const videoRefs = useRef(new Map());
  const detailScaleStageRef = useRef(null);
  const detailScaleContentRef = useRef(null);

  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState("");
  const [similarProducts, setSimilarProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState("");
  const [sizeError, setSizeError] = useState("");
  const [purchaseError, setPurchaseError] = useState("");
  const [addedToCart, setAddedToCart] = useState(false);
  const [addedSimilarProductId, setAddedSimilarProductId] = useState("");
  const [inWishlist, setInWishlist] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [productNotFound, setProductNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState("description");

  const [isZoomEnabled, setIsZoomEnabled] = useState(false);
  const [isInlineZoomVisible, setIsInlineZoomVisible] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
  const [mainMediaAspectRatio, setMainMediaAspectRatio] = useState(
    DEFAULT_MAIN_MEDIA_ASPECT_RATIO
  );
  const [mainMediaSurface, setMainMediaSurface] = useState(DEFAULT_MAIN_MEDIA_SURFACE);
  const [mainMediaFramePreset, setMainMediaFramePreset] = useState(
    DEFAULT_MAIN_MEDIA_FRAME_PRESET
  );
  const [mainMediaFocusPoint, setMainMediaFocusPoint] = useState(
    DEFAULT_MAIN_MEDIA_FOCUS_POINT
  );
  const [mainMediaContentScale, setMainMediaContentScale] = useState(
    DEFAULT_MAIN_MEDIA_CONTENT_SCALE
  );
  const [detailScaleMetrics, setDetailScaleMetrics] = useState({
    isEnabled: false,
    scale: 1,
    height: null,
  });

  const INLINE_ZOOM_SCALE = 2.3;
  const canUseInlineZoom =
    typeof window === "undefined" || !window.matchMedia
      ? true
      : window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  const safePlayVideo = async (videoElement) => {
    if (!videoElement) {
      return;
    }

    try {
      await videoElement.play();
    } catch (error) {
      console.log("Video play prevented:", error.message);
    }
  };

  const safePauseVideo = (videoElement) => {
    if (!videoElement) {
      return;
    }

    try {
      videoElement.pause();
    } catch (error) {
      console.log("Video pause prevented:", error.message);
    }
  };

  const resetInlineZoom = useCallback(() => {
    setIsInlineZoomVisible(false);
    setZoomPosition({ x: 50, y: 50 });
  }, []);

  const updateDetailScaleMetrics = useCallback(() => {
    setDetailScaleMetrics((currentValue) =>
      !currentValue.isEnabled
      && currentValue.scale === 1
      && currentValue.height == null
        ? currentValue
        : { isEnabled: false, scale: 1, height: null }
    );
  }, []);

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !product?._id) {
      return undefined;
    }

    let frameId = null;
    const scheduleScaleUpdate = () => {
      if (frameId != null) {
        window.cancelAnimationFrame(frameId);
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        updateDetailScaleMetrics();
      });
    };

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            scheduleScaleUpdate();
          });

    if (detailScaleStageRef.current) {
      resizeObserver?.observe(detailScaleStageRef.current);
    }

    if (detailScaleContentRef.current) {
      resizeObserver?.observe(detailScaleContentRef.current);
    }

    scheduleScaleUpdate();
    window.addEventListener("resize", scheduleScaleUpdate);

    return () => {
      if (frameId != null) {
        window.cancelAnimationFrame(frameId);
      }

      window.removeEventListener("resize", scheduleScaleUpdate);
      resizeObserver?.disconnect();
    };
  }, [product?._id, updateDetailScaleMetrics]);

  const updateInlineZoomPosition = useCallback((clientX, clientY) => {
    if (!mainImageRef.current) {
      return;
    }

    const rect = mainImageRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(clientY - rect.top, rect.height));

    setZoomPosition({
      x: (x / rect.width) * 100,
      y: (y / rect.height) * 100,
    });
  }, []);

  const handleImageMouseEnter = useCallback(
    (event) => {
      if (!canUseInlineZoom || !isZoomEnabled || isVideo(selectedImage)) {
        return;
      }

      setIsInlineZoomVisible(true);
      updateInlineZoomPosition(event.clientX, event.clientY);
    },
    [canUseInlineZoom, isZoomEnabled, selectedImage, updateInlineZoomPosition]
  );

  const handleImageMouseMove = useCallback(
    (event) => {
      if (!canUseInlineZoom || !isZoomEnabled || isVideo(selectedImage)) {
        return;
      }

      setIsInlineZoomVisible(true);
      updateInlineZoomPosition(event.clientX, event.clientY);
    },
    [canUseInlineZoom, isZoomEnabled, selectedImage, updateInlineZoomPosition]
  );

  const handleImageMouseLeave = useCallback(() => {
    resetInlineZoom();
  }, [resetInlineZoom]);

  const handleMainImageClick = useCallback(
    (event) => {
      if (!canUseInlineZoom || isVideo(selectedImage)) {
        return;
      }

      if (isZoomEnabled) {
        setIsZoomEnabled(false);
        resetInlineZoom();
        return;
      }

      setIsZoomEnabled(true);
      setIsInlineZoomVisible(true);
      updateInlineZoomPosition(event.clientX, event.clientY);
    },
    [
      canUseInlineZoom,
      isZoomEnabled,
      selectedImage,
      resetInlineZoom,
      updateInlineZoomPosition,
    ]
  );

  const fetchProductDetail = useCallback(async () => {
    try {
      const productResponse = await axios.get(`${API_BASE}/products/${id}`);
      const fetchedProduct = productResponse.data;

      setProductNotFound(false);
      setProduct(fetchedProduct);
      setSelectedImage(fetchedProduct?.images?.[0] || "");
      setQuantity(1);
      setSelectedSize("");
      setSizeError("");
      setPurchaseError("");
      setActiveTab("description");
      setAddedToCart(false);
      setAddedSimilarProductId("");
      setIsImageModalOpen(false);

      if (fetchedProduct?._id) {
        setInWishlist(isInWishlist(fetchedProduct._id));
      }
    } catch (error) {
      console.error("Error fetching product:", error);

      if (error?.response?.status === 404) {
        setProductNotFound(true);
        setProduct(null);
        setSelectedImage("");
      }
    }
  }, [id, isInWishlist]);

  useEffect(() => {
    if (location.state && location.state.refreshReviews) {
      fetchProductDetail();
      window.history.replaceState({}, document.title);
      return;
    }

    fetchProductDetail();
  }, [fetchProductDetail, location.state]);

  useEffect(() => {
    const fetchSimilarProducts = async () => {
      try {
        const response = await axios.get(`${API_BASE}/products`, {
          params: {
            material: product?.material,
            product: product?.type,
            limit: 12,
          },
        });

        const filteredProducts = response.data.filter(
          (item) => item._id !== product._id
        );
        setSimilarProducts(filteredProducts);
      } catch (error) {
        console.error("Error fetching similar products:", error);
      }
    };

    if (product?._id) {
      fetchSimilarProducts();
    }
  }, [product]);

  useEffect(() => {
    const handleRefresh = () => fetchProductDetail();
    window.addEventListener("refreshProductReviews", handleRefresh);

    return () =>
      window.removeEventListener("refreshProductReviews", handleRefresh);
  }, [fetchProductDetail]);

  useEffect(
    () => bindStorefrontRefresh(fetchProductDetail, { scopes: ["products"] }),
    [fetchProductDetail]
  );

  useEffect(() => {
    const currentVideoRefs = videoRefs.current;

    return () => {
      currentVideoRefs.forEach((videoElement) => {
        if (videoElement) {
          try {
            videoElement.pause();
          } catch (error) {
            console.log("Video pause prevented:", error.message);
          }
        }
      });
      currentVideoRefs.clear();
    };
  }, []);

  useEffect(() => {
    setIsZoomEnabled(false);
    resetInlineZoom();
  }, [selectedImage, resetInlineZoom]);

  useEffect(() => {
    mainMediaPresentationCacheRef.current.clear();
  }, [product?._id]);

  const mediaItems = Array.isArray(product?.images)
    ? product.images.filter(Boolean)
    : [];
  const selectedMedia = selectedImage || mediaItems[0] || "";
  const descriptionPoints = getDescriptionPoints(product);
  const precautionPoints = getPrecautionPoints(product);
  const shippingCardItems = getShippingCardItems();
  const availableSizes = Array.isArray(product?.availableSizes)
    ? product.availableSizes
        .map((size) => String(size || "").trim())
        .filter(Boolean)
    : [];
  const stockQuantity = getStockQuantity(product?.stockQuantity);
  const isOutOfStock = stockQuantity === 0;
  const isLowStock = stockQuantity > 0 && stockQuantity <= LOW_STOCK_THRESHOLD;
  const currentPrice = Number.isFinite(Number(product?.offerPrice))
    ? Number(product.offerPrice)
    : Number(product?.price) || 0;
  const displaySku = getDisplaySku(product);
  const lowStockInlineLabel =
    stockQuantity === 1
      ? "Only 1 item left in stock"
      : `Only ${stockQuantity} items left in stock`;
  const whyYoullLoveThis = [
    "Premium quality finish for a refined look",
    "Elegant design that feels occasion-ready",
    "Comfortable wear for long hours of styling",
  ];

  const applyMainMediaPresentation = useCallback((presentation) => {
    const nextPresentation = presentation || DEFAULT_MAIN_MEDIA_PRESENTATION;

    setMainMediaAspectRatio((currentRatio) =>
      Math.abs(currentRatio - nextPresentation.aspectRatio) < 0.001
        ? currentRatio
        : nextPresentation.aspectRatio
    );
    setMainMediaFramePreset((currentPreset) =>
      currentPreset === nextPresentation.framePreset
        ? currentPreset
        : nextPresentation.framePreset
    );
    setMainMediaSurface((currentSurface) =>
      currentSurface === nextPresentation.surface
        ? currentSurface
        : nextPresentation.surface
    );
    setMainMediaFocusPoint((currentFocusPoint) =>
      currentFocusPoint.x === nextPresentation.focusX
      && currentFocusPoint.y === nextPresentation.focusY
        ? currentFocusPoint
        : { x: nextPresentation.focusX, y: nextPresentation.focusY }
    );
    setMainMediaContentScale((currentScale) =>
      Math.abs(currentScale - nextPresentation.contentScale) < 0.001
        ? currentScale
        : nextPresentation.contentScale
    );
  }, []);

  useEffect(() => {
    if (!selectedMedia || isVideo(selectedMedia)) {
      applyMainMediaPresentation(DEFAULT_MAIN_MEDIA_PRESENTATION);
      return;
    }

    const cachedPresentation = mainMediaPresentationCacheRef.current.get(selectedMedia);

    if (cachedPresentation) {
      applyMainMediaPresentation(cachedPresentation);
    }
  }, [applyMainMediaPresentation, selectedMedia]);

  const handleMainProductImageLoad = useCallback((event) => {
    const nextAspectRatio =
      event.currentTarget.naturalWidth > 0 && event.currentTarget.naturalHeight > 0
        ? event.currentTarget.naturalWidth / event.currentTarget.naturalHeight
        : DEFAULT_MAIN_MEDIA_ASPECT_RATIO;
    const nextPreset = getMainMediaFramePreset(
      event.currentTarget.naturalWidth,
      event.currentTarget.naturalHeight
    );
    const nextSurface = getMainMediaSurfaceColor(event.currentTarget);
    const nextContentFit = getMainMediaContentFit(event.currentTarget);
    const nextPresentation = {
      aspectRatio: nextAspectRatio,
      focusX: nextContentFit.focusX,
      focusY: nextContentFit.focusY,
      framePreset: nextPreset,
      contentScale: nextContentFit.contentScale,
      surface: nextSurface,
    };
    const mediaCacheKey =
      event.currentTarget.currentSrc
      || event.currentTarget.getAttribute("src")
      || selectedMedia;

    if (selectedMedia) {
      mainMediaPresentationCacheRef.current.set(selectedMedia, nextPresentation);
    }

    if (mediaCacheKey && mediaCacheKey !== selectedMedia) {
      mainMediaPresentationCacheRef.current.set(mediaCacheKey, nextPresentation);
    }

    applyMainMediaPresentation(nextPresentation);
  }, [applyMainMediaPresentation, selectedMedia]);

  useEffect(() => {
    if (stockQuantity > 0 && quantity > stockQuantity) {
      setQuantity(stockQuantity);
    }
  }, [quantity, stockQuantity]);

  const handleAddToCart = () => {
    if (isOutOfStock) {
      setPurchaseError("Out of stock. Please wait for fresh inventory.");
      return;
    }

    if (availableSizes.length > 0 && !selectedSize) {
      setSizeError("Please select a size before adding this piece to your cart.");
      return;
    }

    if (quantity > stockQuantity) {
      setPurchaseError(`Only ${stockQuantity} item(s) left in stock.`);
      return;
    }

    const success = addToCart(product, quantity, selectedSize);

    if (success) {
      setSizeError("");
      setPurchaseError("");
      setAddedToCart(true);
      window.setTimeout(() => setAddedToCart(false), 2000);
    }
  };

  const handleBuyNow = () => {
    if (isOutOfStock) {
      setPurchaseError("Out of stock. Please wait for fresh inventory.");
      return;
    }

    if (availableSizes.length > 0 && !selectedSize) {
      setSizeError("Please select a size before continuing.");
      return;
    }

    if (quantity > stockQuantity) {
      setPurchaseError(`Only ${stockQuantity} item(s) left in stock.`);
      return;
    }

    const success = addToCart(product, quantity, selectedSize);

    if (success) {
      setSizeError("");
      setPurchaseError("");
      navigate("/cart");
    }
  };

  const handleWishlistToggle = () => {
    if (!product?._id) {
      return;
    }

    if (inWishlist) {
      removeFromWishlist(product._id);
      setInWishlist(false);
      return;
    }

    const success = addToWishlist(product);

    if (success) {
      setInWishlist(true);
    }
  };

  const handleShareProduct = async () => {
    const productUrl = typeof window !== "undefined" ? window.location.href : "";
    const shareData = {
      title: product?.title || "Product",
      text: `Take a look at ${product?.title || "this jewellery piece"}.`,
      url: productUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }

      if (navigator.clipboard && productUrl) {
        await navigator.clipboard.writeText(productUrl);
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.log("Product share failed:", error.message);
      }
    }
  };

  const handleSimilarProductAction = (similarProduct) => {
    const similarStockQuantity = getStockQuantity(similarProduct?.stockQuantity);
    const requiresSizeSelection =
      Array.isArray(similarProduct?.availableSizes) &&
      similarProduct.availableSizes.filter((size) => String(size || "").trim()).length > 0;

    if (similarStockQuantity === 0) {
      return;
    }

    if (requiresSizeSelection) {
      saveCurrentScrollPosition(location.key);
      navigate(`/product/${similarProduct._id}`);
      return;
    }

    if (isInCart(similarProduct._id)) {
      navigate("/cart");
      return;
    }

    const success = addToCart(similarProduct, 1);

    if (success) {
      setAddedSimilarProductId(similarProduct._id);
      window.setTimeout(() => {
        setAddedSimilarProductId((currentId) =>
          currentId === similarProduct._id ? "" : currentId
        );
      }, 1800);
    }
  };

  const incrementQuantity = () => {
    setPurchaseError("");
    setQuantity((currentValue) =>
      currentValue < stockQuantity ? currentValue + 1 : currentValue
    );
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setPurchaseError("");
      setQuantity((currentValue) => currentValue - 1);
    }
  };

  const openImageModal = () => {
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeImageModal();
      }
    };

    if (isImageModalOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isImageModalOpen]);

  if (productNotFound) {
    return <div className="product-detail-state">Product not found.</div>;
  }

  if (!product) {
    return <div className="product-detail-state">Loading product...</div>;
  }

  const isSelectedMediaVideo = isVideo(selectedMedia);
  const isWhiteSurfaceMainMedia =
    isSelectedMediaVideo || mainMediaSurface === DEFAULT_MAIN_MEDIA_SURFACE;
  const editorialMainMediaScaleBoost = isWhiteSurfaceMainMedia ? 1 : 1.08;
  const baseMainMediaScale = isSelectedMediaVideo
    ? DEFAULT_MAIN_MEDIA_CONTENT_SCALE
    : mainMediaContentScale * editorialMainMediaScaleBoost;
  const isInlineZoomActive =
    canUseInlineZoom && !isSelectedMediaVideo && isZoomEnabled && isInlineZoomVisible;
  const shouldShowSelectionCard = true;
  const mainImageZoomStyle =
    !isSelectedMediaVideo
      ? {
          objectPosition: `${mainMediaFocusPoint.x}% ${mainMediaFocusPoint.y}%`,
          transform: `scale(${baseMainMediaScale * (isInlineZoomActive ? INLINE_ZOOM_SCALE : 1)})`,
          transformOrigin: isInlineZoomActive
            ? `${zoomPosition.x}% ${zoomPosition.y}%`
            : `${mainMediaFocusPoint.x}% ${mainMediaFocusPoint.y}%`,
        }
      : undefined;
  const mainMediaFrameClassName = [
    "main-media-frame",
    `main-media-frame--${isSelectedMediaVideo ? DEFAULT_MAIN_MEDIA_FRAME_PRESET : mainMediaFramePreset}`,
    isWhiteSurfaceMainMedia ? "main-media-frame--white-surface" : "main-media-frame--editorial",
    mediaItems.length <= 1 ? "main-media-frame--full-width" : "",
    selectedMedia && !isSelectedMediaVideo && canUseInlineZoom ? "zoomable-frame" : "",
    isZoomEnabled ? "zoom-enabled" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const mainMediaFrameStyle = {
    "--pdp-main-media-aspect-ratio": isSelectedMediaVideo
      ? DEFAULT_MAIN_MEDIA_ASPECT_RATIO
      : mainMediaAspectRatio,
    "--pdp-main-media-backdrop-image":
      isSelectedMediaVideo || !selectedMedia ? "none" : toCssUrlValue(selectedMedia),
    "--pdp-main-media-surface": isSelectedMediaVideo
      ? DEFAULT_MAIN_MEDIA_SURFACE
      : mainMediaSurface,
  };
  const isDetailScaleEnabled = detailScaleMetrics.isEnabled;
  const isDetailScaled = isDetailScaleEnabled && detailScaleMetrics.scale < 0.999;
  const detailScaleStageStyle =
    isDetailScaled && detailScaleMetrics.height != null
      ? { height: `${detailScaleMetrics.height}px` }
      : undefined;
  const detailScaleContentStyle = isDetailScaleEnabled
    ? { "--pdp-detail-scale": detailScaleMetrics.scale }
    : undefined;

  return (
    <div className="product-detail-wrapper">
      <div className="product-detail-shell">
        <div
          ref={detailScaleStageRef}
          className={`product-detail-scale-stage ${isDetailScaled ? "is-scaled" : ""}`}
          style={detailScaleStageStyle}
        >
          <div
            ref={detailScaleContentRef}
            className={`product-detail-scale-content ${isDetailScaleEnabled ? "is-enabled" : ""} ${
              isDetailScaled ? "is-scaled" : ""
            }`}
            style={detailScaleContentStyle}
          >
            <div className="product-detail-container">
              <div className="detail-actions" aria-label="Product actions">
                <button
                  type="button"
                  className="detail-action-button"
                  onClick={handleShareProduct}
                  aria-label="Share product"
                >
                  <FaShareAlt />
                </button>
              </div>

          <section className={`image-section ${mediaItems.length <= 1 ? "image-section--single" : ""}`}>
            {mediaItems.length > 1 ? (
              <div className="thumbnail-images" aria-label="Product media gallery">
                {mediaItems.map((media, index) => (
                  <div
                    key={`${media}-${index}`}
                    className={`thumbnail-wrapper ${selectedMedia === media ? "is-active" : ""}`}
                  >
                    {isVideo(media) ? (
                      <video
                        src={media}
                        className={`thumbnail-video ${selectedMedia === media ? "active" : ""}`}
                        onClick={() => setSelectedImage(media)}
                        muted
                        onError={(event) => {
                          console.log("Thumbnail video error:", event.target.error);
                        }}
                        ref={(element) => {
                          if (element) {
                            videoRefs.current.set(`thumb-${index}`, element);
                          } else {
                            videoRefs.current.delete(`thumb-${index}`);
                          }
                        }}
                      />
                    ) : (
                      <img
                        src={media}
                        alt={`${product.title} preview ${index + 1}`}
                        className={selectedMedia === media ? "active" : ""}
                        onClick={() => setSelectedImage(media)}
                        loading="lazy"
                      />
                    )}
                    {isVideo(media) ? (
                      <div className="video-indicator">Video</div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}

            <div
              className={mainMediaFrameClassName}
              ref={mainImageRef}
              style={mainMediaFrameStyle}
              onClick={selectedMedia && !isSelectedMediaVideo ? handleMainImageClick : undefined}
              onDoubleClick={selectedMedia && !isSelectedMediaVideo ? openImageModal : undefined}
              onMouseEnter={selectedMedia && !isSelectedMediaVideo ? handleImageMouseEnter : undefined}
              onMouseLeave={selectedMedia && !isSelectedMediaVideo ? handleImageMouseLeave : undefined}
              onMouseMove={selectedMedia && !isSelectedMediaVideo ? handleImageMouseMove : undefined}
            >
              <button
                type="button"
                className={`detail-action-button image-wishlist-button ${inWishlist ? "is-active" : ""}`}
                onClick={(event) => {
                  event.stopPropagation();
                  handleWishlistToggle();
                }}
                aria-label={inWishlist ? "Remove from wishlist" : "Add to wishlist"}
              >
                {inWishlist ? <FaHeart /> : <FaRegHeart />}
              </button>

              {isSelectedMediaVideo ? (
                <video
                  src={selectedMedia}
                  controls
                  className="main-video"
                  onClick={openImageModal}
                  onError={(event) => {
                    console.log("Main video error:", event.target.error);
                  }}
                  ref={(element) => {
                    if (element) {
                      videoRefs.current.set("main", element);
                    } else {
                      videoRefs.current.delete("main");
                    }
                  }}
                />
              ) : selectedMedia ? (
                <img
                  src={selectedMedia}
                  alt={product.title}
                  className="main-product-image"
                  draggable={false}
                  onLoad={handleMainProductImageLoad}
                  style={mainImageZoomStyle}
                />
              ) : (
                <div className="main-image-placeholder">Media unavailable</div>
              )}
            </div>
          </section>

          <aside className="detail-section">
            <div className="detail-header">
              <h1>{product.title}</h1>
            </div>

            <div className="price-rating-row">
              <div className="price-block">
                <div className="price-box">
                  <span className="offer-price">{formatCurrency(currentPrice)}</span>
                  <div className="sku-stock-row">
                    <span className="product-sku">SKU {displaySku}</span>
                    {!isOutOfStock && isLowStock ? (
                      <span className="stock-inline-note">{lowStockInlineLabel}</span>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            {isOutOfStock ? (
              <p className="stock-status stock-status--out">
                Currently out of stock. Please check back soon.
              </p>
            ) : null}

            <div className="why-love-action-row">
            <div className="why-love-section">
              <span className="why-love-kicker">Why you'll love this</span>
              <div className="why-love-list">
                {whyYoullLoveThis.map((item) => (
                  <div key={item} className="why-love-item">
                    <span className="why-love-bullet" aria-hidden="true">
                      •
                    </span>
                    <p>{item}</p>
                  </div>
                ))}
              </div>
            </div>
              <div className="button-group desktop-cta-group">
                {isOutOfStock ? (
                  <button className="out-of-stock-btn" type="button" disabled>
                    Out of Stock
                  </button>
                ) : (
                  <>
                    <button className="add-to-cart" type="button" onClick={handleAddToCart}>
                      {addedToCart ? "Added" : "Add to Cart"}
                    </button>
                    <button className="buy-now" type="button" onClick={handleBuyNow}>
                      Buy Now
                    </button>
                  </>
                )}
              </div>
            </div>

            {shouldShowSelectionCard ? (
              <div className="selection-card">
                <div className="selection-controls-row">
                  {availableSizes.length > 0 ? (
                    <div className="size-selector">
                      <div className="selector-heading">
                        <label>Size{selectedSize ? `: ${selectedSize}` : ""}</label>
                      </div>

                      <div className="size-options">
                        {availableSizes.map((sizeValue) => (
                          <button
                            key={sizeValue}
                            type="button"
                            className={`size-option ${selectedSize === sizeValue ? "is-selected" : ""}`}
                            onClick={() => {
                              setSelectedSize(sizeValue);
                              setSizeError("");
                              setPurchaseError("");
                            }}
                          >
                            {sizeValue}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="size-selector size-selector--static">
                      <div className="selector-heading">
                        <label>Size: NA</label>
                      </div>
                    </div>
                  )}

                  {!isOutOfStock ? (
                    <div className="quantity-selector">
                      <div className="selector-heading">
                        <label>Quantity</label>
                      </div>

                      <div className="quantity-controls">
                        <button
                          type="button"
                          onClick={decrementQuantity}
                          disabled={quantity <= 1}
                          aria-label="Decrease quantity"
                        >
                          -
                        </button>
                        <span>{quantity}</span>
                        <button
                          type="button"
                          onClick={incrementQuantity}
                          disabled={quantity >= stockQuantity}
                          aria-label="Increase quantity"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>

                {sizeError ? <p className="size-error-message">{sizeError}</p> : null}
                {purchaseError ? <p className="size-error-message">{purchaseError}</p> : null}
              </div>
            ) : null}
          </aside>
            </div>
          </div>
        </div>

        <section className="product-tabs-section">
          <div className="section-heading">
            <span className="section-kicker">Product Details</span>
            <h2>Everything you need before you commit</h2>
          </div>

          <div className="product-tabs">
            <div className="product-tab-list" role="tablist" aria-label="Product details tabs">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "description"}
                className={activeTab === "description" ? "active" : ""}
                onClick={() => setActiveTab("description")}
              >
                Description
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "precautions"}
                className={activeTab === "precautions" ? "active" : ""}
                onClick={() => setActiveTab("precautions")}
              >
                Precautions
              </button>
            </div>

            {activeTab === "description" ? (
              <div className="product-tab-panel product-tab-panel--description">
                <div className="tab-column">
                  <h3>Product description</h3>
                  <ul className="detail-bullet-list">
                    {descriptionPoints.map((point, index) => (
                      <li key={`${point}-${index}`}>{point}</li>
                    ))}
                  </ul>
                </div>

                <aside className="shipping-detail-card">
                  <span className="shipping-detail-kicker">Shipping details</span>
                  <h3>Delivery information</h3>
                  <ul className="shipping-detail-list">
                    {shippingCardItems.map((item, index) => (
                      <li key={`${item}-${index}`}>
                        <span className="shipping-detail-dot" aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </aside>
              </div>
            ) : activeTab === "precautions" ? (
              <div className="product-tab-panel product-tab-panel--precautions">
                <aside className="detail-info-card detail-info-card--precautions">
                  <span className="detail-info-kicker">Care notes</span>
                  <h3>Precautions</h3>
                  <ul className="detail-card-list">
                    {precautionPoints.map((item, index) => (
                      <li key={`${item}-${index}`}>
                        <span className="detail-card-dot" aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </aside>
              </div>
            ) : null}
          </div>
        </section>

        {similarProducts.length > 0 ? (
          <section className="similar-products-section">
            <div className="section-heading">
              <span className="section-kicker">Curated Suggestions</span>
              <h2>You May Also Like</h2>
            </div>

            <Swiper
              modules={[Navigation]}
              className="similar-products-slider"
              initialSlide={getCarouselPosition(location.key, "similar-products")}
              onSlideChange={(swiper) =>
                saveCarouselPosition(location.key, "similar-products", swiper.activeIndex)
              }
              spaceBetween={18}
              slidesPerView={4}
              navigation
              breakpoints={{
                0: { slidesPerView: 1.1 },
                640: { slidesPerView: 2.1 },
                980: { slidesPerView: 3.1 },
                1280: { slidesPerView: 4 },
              }}
            >
              {similarProducts.map((similarProduct) => {
                const primaryMedia = similarProduct.images?.[0];
                const similarStockQuantity = getStockQuantity(
                  similarProduct?.stockQuantity
                );
                const similarOutOfStock = similarStockQuantity === 0;
                const requiresSizeSelection =
                  Array.isArray(similarProduct?.availableSizes) &&
                  similarProduct.availableSizes.filter((size) =>
                    String(size || "").trim()
                  ).length > 0;
                const quickActionLabel = similarOutOfStock
                  ? "Out of Stock"
                  : requiresSizeSelection
                  ? "Select Size"
                  : isInCart(similarProduct._id)
                  ? "Go to Cart"
                  : addedSimilarProductId === similarProduct._id
                  ? "Added"
                  : "Quick Add";

                return (
                  <SwiperSlide key={similarProduct._id}>
                    <article
                      className="similar-product-card"
                      onClick={() => {
                        saveCurrentScrollPosition(location.key);
                        navigate(`/product/${similarProduct._id}`);
                      }}
                    >
                      <div className="similar-product-media-frame">
                        {isVideo(primaryMedia) ? (
                          <div className="similar-product-video-wrapper">
                            <video
                              src={primaryMedia}
                              className="similar-product-video"
                              muted
                              onMouseEnter={(event) => safePlayVideo(event.target)}
                              onMouseLeave={(event) => safePauseVideo(event.target)}
                              onError={(event) => {
                                console.log(
                                  "Similar product video error:",
                                  event.target.error
                                );
                              }}
                            />
                            <div className="similar-product-video-indicator">Video</div>
                          </div>
                        ) : primaryMedia ? (
                          <img
                            className="similar-product-image"
                            src={primaryMedia}
                            alt={similarProduct.title}
                            loading="lazy"
                          />
                        ) : (
                          <div className="similar-product-empty">No image</div>
                        )}
                      </div>

                      <div className="similar-product-copy">
                        <h3>{similarProduct.title}</h3>

                        <div className="similar-product-pricing">
                          <strong>
                            {formatCurrency(
                              Number.isFinite(Number(similarProduct.offerPrice))
                                ? similarProduct.offerPrice
                                : similarProduct.price
                            )}
                          </strong>
                        </div>

                        <button
                          type="button"
                          className="similar-product-cta"
                          disabled={similarOutOfStock}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleSimilarProductAction(similarProduct);
                          }}
                        >
                          {quickActionLabel}
                        </button>
                      </div>
                    </article>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </section>
        ) : null}
      </div>

      <div className="mobile-sticky-bar">
        {isOutOfStock ? (
          <button className="out-of-stock-btn" type="button" disabled>
            Out of Stock
          </button>
        ) : (
          <>
            <div className="mobile-sticky-price">
              <span>Price</span>
              <strong>{formatCurrency(currentPrice)}</strong>
            </div>
            <div className="mobile-sticky-actions">
              <button className="add-to-cart" type="button" onClick={handleAddToCart}>
                {addedToCart ? "Added" : "Add to Cart"}
              </button>
              <button className="buy-now" type="button" onClick={handleBuyNow}>
                Buy Now
              </button>
            </div>
          </>
        )}
      </div>

      {isImageModalOpen ? (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(event) => event.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeImageModal}>
              <FaTimes />
            </button>

            <div className="modal-image-container">
              {isVideo(selectedMedia) ? (
                <video
                  src={selectedMedia}
                  controls
                  className="modal-video"
                  onError={(event) => {
                    console.log("Modal video error:", event.target.error);
                  }}
                  ref={(element) => {
                    if (element) {
                      videoRefs.current.set("modal-main", element);
                    } else {
                      videoRefs.current.delete("modal-main");
                    }
                  }}
                />
              ) : (
                <img src={selectedMedia} alt={`${product.title} enlarged view`} />
              )}
            </div>

            <div className="modal-thumbnails">
              {mediaItems.map((media, index) => (
                <div
                  key={`${media}-${index}`}
                  className={`modal-thumbnail-wrapper ${selectedMedia === media ? "is-active" : ""}`}
                >
                  {isVideo(media) ? (
                    <video
                      src={media}
                      className={`modal-thumbnail-video ${
                        selectedMedia === media ? "active" : ""
                      }`}
                      onClick={() => setSelectedImage(media)}
                      muted
                      onError={(event) => {
                        console.log(
                          "Modal thumbnail video error:",
                          event.target.error
                        );
                      }}
                      ref={(element) => {
                        if (element) {
                          videoRefs.current.set(`modal-thumb-${index}`, element);
                        } else {
                          videoRefs.current.delete(`modal-thumb-${index}`);
                        }
                      }}
                    />
                  ) : (
                    <img
                      src={media}
                      alt={`${product.title} thumbnail ${index + 1}`}
                      className={selectedMedia === media ? "active" : ""}
                      onClick={() => setSelectedImage(media)}
                    />
                  )}
                  {isVideo(media) ? (
                    <div className="modal-video-indicator">Video</div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ProductDetail;
